import type { JSX } from 'react'
import type { Collection } from './CollectionTag'
import type { RoomsParticipantDistribution } from '@/domain/shared/types'
import type { SearchResult } from '@/scopes/search/search/infrastructure/api/types'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import {
  mqMin,
  space,
  tok,
  tokPx,
} from '@sunwebgroup/ui-components'
import { Button, focusOutline, Tag, Typography, useBreakpoints } from '@sunwebgroup/ui-components/uds'
import { css, styled } from 'styled-components'
import { useIntl } from '@/intl'

import { Card } from '@/scopes/common/components/Card'
import { PriceInfo } from '@/scopes/common/components/PriceInfo'
import {
  getAccommodationServices,
} from '@/scopes/search/search/domain/product-card/transformIncludedServices'
import {
  AirportDropdown,
} from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/AirportDropdown'
import { CampaignUsp } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/CampaignUsp'
import * as List from '../ListItem'
import { CollectionTag } from './CollectionTag'
import FavoriteButton from './FavoriteButton'
import { ImageCarousel } from './ImageCarousel'
import { LocationTag } from './LocationTag'
import { OverlayLink } from './OverlayLink'
import { ScoreRatings } from './ScoresRatings'
import { SubscribeLink } from './SubscribeLink'

export interface ProductCardProps {
  result: SearchResult
  participants?: RoomsParticipantDistribution
  onClick: () => void
  showAirportDropdown: boolean
}

const LIST_ITEM_ICON_SIZE = 16

const PriceBoxWrapper = styled.div``

const StyledProductSearchResultCard = styled(Card)`
  color: ${tok('semantics_content_color')};
  position: relative;
  overflow: unset;

  ${PriceBoxWrapper} {
    ${Tag} {
      position: absolute;
      transform: translateY(-100%);
      pointer-events: none; // make it possible to click on the link
    }
  }
`

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  border-top-left-radius: ${tokPx('semantics_cards_corner_radius')};
  border-top-right-radius: ${tokPx('semantics_cards_corner_radius')};

  > ${Tag} {
    left: ${space('primitives_space_12')};
    position: absolute;
    pointer-events: none;
    top: ${space('primitives_space_12')};
    max-width: 75%;

    :last-child {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  ${FavoriteButton} {
    position: absolute;
    top: ${space('primitives_space_16')};
    right: ${space('primitives_space_16')};
  }

  @media (hover: hover) {
    &:hover {
      button {
        display: inline-flex;
      }
    }
  }

  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-top-right-radius: unset;
    border-bottom-left-radius: ${tokPx('semantics_cards_corner_radius')};
  }
`

const TextsWrapper = styled.div`
  display: grid;
  flex: 1;
  padding: ${space('primitives_space_20')};
  ${focusOutline()}
`

const UnorderedList = styled(List.Root)`
  margin-top: ${space('primitives_space_16')};
  margin-bottom: ${space('primitives_space_24')};
  gap: ${space('primitives_space_6')};
  display: flex;
  flex-direction: column;
`

const PackageBlockWrapper = styled.div``

const PackageBlockAndPriceBoxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${space('primitives_space_8')};
`

const StyledCollectionTag = styled(CollectionTag)`
  position: absolute;
  top: ${space('primitives_space_12')};
  left: ${space('primitives_space_12')};
  pointer-events: none;
`

ProductCard.modSize = (minWidth: 'tablet' | 'desktop') => {
  switch (minWidth) {
    case 'tablet':
      return css`
        ${ImageWrapper} {
          max-width: 255px;
          height: auto;
        }

        ${StyledProductSearchResultCard} {
          display: flex;
        }

        ${UnorderedList} {
          max-width: 75%;
        }
      `
    case 'desktop':
      return css`
        ${ImageWrapper} {
          flex-shrink: 0;
          max-width: 320px;
          height: auto;
        }
      `
  }
}

const AccoName = styled(Typography)`
  ${Typography.modVariant('display-i-regular')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    ${Typography.modVariant('display-g-regular')};
  }
`

const Item = styled(List.Item)`
  align-items: center;
`

const ProductCardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  align-content: flex-start;
  gap: ${space('primitives_space_8')};
  align-self: stretch;
  flex-wrap: wrap;

  margin-top: ${space('primitives_space_16')};

  ${Button} {
    min-width: ${tokPx('primitives_area_size_160')};
  }
`
const ViewButton = styled(Button)`
  position: initial;
    width: 100%;
  @media (min-width: ${tokPx('primitives_breakpoint_m_min')}) {
    width: unset;
  }
`

export function ProductCard({
  result,
  participants,
  onClick,
  showAirportDropdown,
}: ProductCardProps): JSX.Element {
  const {
    id: accommodationId,
    price,
    featuredFilters,
    departureDateDetail: departureDate,
    campaign,
    urlWithFilters: href,
    images: imageSources,
    isUnavailable,
    locations,
    name = '',
    rating = 0,
    userRating,
    userRatingText,
    usps,
    outboundDepartureAirportCode,
    outboundDepartureAirportName,
    collection,
    isMarketExclusive,
    isSustainable,
  } = result
  const intl = useIntl()
  const { isDesktop } = useBreakpoints()
  const translations = {
    skiMaterial: intl.formatMessage({ id: 'product_card_ski_skimaterial_included' }),
    skiPass: intl.formatMessage({ id: 'product_card_ski_skipass_included' }),
    skiPassWithPrice: intl.formatMessage(
      { id: 'product_card_ski_skipass_included_price' },
      { price: price?.skiPassPublicPrice ? intl.formatTruncatedCurrency(price.skiPassPublicPrice) : '' },
    ),
    skiPassWithSkiMaterial: intl.formatMessage({ id: 'product_card_ski_skipass_skimaterial_included' }),
    skiPassWithPriceAndSkiMaterial: intl.formatMessage(
      { id: 'product_card_ski_skipass_skimaterial_included_price' },
      { price: price?.skiPassPublicPrice ? intl.formatTruncatedCurrency(price.skiPassPublicPrice) : '' },
    ),
  }
  const accommodationServices = getAccommodationServices(translations, price)
  const yieldCampaign = price?.yieldCampaignName
  const averagePrice = price?.averagePrice
  const priceDiscounts = price?.priceDiscounts
  const partyComposition = featuredFilters?.occupancy
  const mealPlan = featuredFilters?.mealplan
  const collectionType = (collection?.id.toLowerCase() || 'default') as Collection

  return (
    <StyledProductSearchResultCard qa-product-card="true" data-qa="product-card" onClick={onClick}>
      {/* OverlayLink is intentionally empty, because the whole card should be clickable, but also the image carousel should be scrollable & buttons clickable */}
      <OverlayLink href={href} role="button" aria-label={name} target={isDesktop ? '_blank' : '_self'} />
      <ImageWrapper>
        <ImageCarousel
          imageIndex={accommodationId}
          images={imageSources.map(imageSrc => ({
            src: imageSrc,
            alt: name,
          }))}
          href={href}
        />
        {yieldCampaign
          ? (
              <Tag variant="primary">
                {yieldCampaign}
              </Tag>
            )
          : (
              collection && (
                <StyledCollectionTag collectionName={collectionType}>
                  {collection.name}
                </StyledCollectionTag>
              )
            )}
        <FavoriteButton result={result} />
      </ImageWrapper>
      <TextsWrapper>
        <AccoName forwardedAs="span">
          {name}
        </AccoName>
        <ScoreRatings rating={rating} userRating={userRating} aria-label={userRatingText} />
        <LocationTag locations={locations} />
        <UnorderedList>
          {accommodationServices.map(service => (
            <Item $icon={service.icon} key={service.key} $size={LIST_ITEM_ICON_SIZE}>
              <Typography variant="body-s-regular-tight">{service.text}</Typography>
            </Item>
          ))}
          {isMarketExclusive && (
            <Item $icon="LogoSunweb" $size={LIST_ITEM_ICON_SIZE} data-qa="sunweb-only-usp">
              <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'product_card_sunweb_exclusive' })}</Typography>
            </Item>
          )}
          {isSustainable && (
            <Item $icon="Leaf" $size={LIST_ITEM_ICON_SIZE} data-qa="sustainability-usp">
              <Typography variant="body-s-regular-tight">{intl.formatMessage({ id: 'product_card_sustainable' })}</Typography>
            </Item>
          )}
          <CampaignUsp campaign={campaign} />
          {usps.map((uspText, index) => (
            <List.Item data-qa="acco-usp" $size={LIST_ITEM_ICON_SIZE} key={index}>
              {uspText}
            </List.Item>
          ))}
        </UnorderedList>
        {isUnavailable
          ? (
              <Banner color="warning" size="M" hasIcon={false} contentIsCentered={false}>
                <Banner.Text>
                  {intl.formatMessage(
                    { id: 'product_card_unavailable_banner_text' },
                    {
                      a1: (chunks: (string | JSX.Element)[]) => (
                        <SubscribeLink accommodationId={accommodationId} linkContent={chunks} />
                      ),
                    },
                  )}
                </Banner.Text>
              </Banner>
            )
          : (
              <PackageBlockAndPriceBoxWrapper>
                <PackageBlockWrapper>
                  <Typography forwardedAs="div" variant="body-s-bold-snug">
                    {`${departureDate} | ${featuredFilters?.duration || ''}`}
                  </Typography>
                  {mealPlan && partyComposition && (
                    <Typography forwardedAs="div" variant="body-s-regular-snug">
                      {mealPlan}
                      ,
                      {' '}
                      {partyComposition}
                    </Typography>
                  )}
                  {showAirportDropdown && outboundDepartureAirportCode && outboundDepartureAirportName && (
                    <AirportDropdown
                      airportCode={outboundDepartureAirportCode}
                      airportName={outboundDepartureAirportName}
                    />
                  )}
                </PackageBlockWrapper>
                {averagePrice && (
                  <PriceBoxWrapper>
                    <PriceInfo
                      accommodationId={accommodationId}
                      averagePrice={averagePrice}
                      priceDiscounts={priceDiscounts}
                      participants={participants}
                      acmInformation={price?.acmInformation}
                    />
                  </PriceBoxWrapper>
                )}
              </PackageBlockAndPriceBoxWrapper>
            )}
        <ProductCardActions>
          <ViewButton>{intl.formatMessage({ id: 'product_card_view_button_text' })}</ViewButton>
        </ProductCardActions>
      </TextsWrapper>
    </StyledProductSearchResultCard>
  )
}

ProductCard.displayName = 'ProductCard'
